import React, { useState } from "react";
import { Upload, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import PageHeader from "@/components/PageHeader";
import api, { trackEvent, formatApiErrorDetail } from "@/lib/api";

export default function SubmitResume() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", current_location: "", qualification: "", experience: "", current_job: "", preferred_job: "", skills: "", preferred_location: "", expected_salary: "", employment_type: "", languages: "" });
  const [resume, setResume] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please accept the consent to proceed.");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      if (photo) fd.append("photo", photo);
      await api.post("/resume-submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackEvent("submit_resume", { preferred_job: form.preferred_job });
      setDone(true);
      toast.success("Resume submitted successfully!");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit"); }
    finally { setLoading(false); }
  };

  if (done) return (
    <PublicLayout>
      <PageHeader eyebrow="Thank You" title="Resume Submitted!" />
      <section className="max-w-xl mx-auto px-4 py-16 text-center">
        <CheckCircle2 size={56} className="text-brand-emerald mx-auto" />
        <p className="text-slate-300 mt-4">Your resume has been added to our candidate database. Our recruiters will reach out to you when we find a matching opportunity.</p>
        <Button className="mt-6 bg-brand-emerald text-white" onClick={() => window.location.assign("/jobs")}>Browse Jobs</Button>
      </section>
    </PublicLayout>
  );

  return (
    <PublicLayout waMessage="Hi Dreamzz HR & Consultancy, I am looking for job opportunities.">
      <Seo title="Submit Your Resume" description="Submit your resume to Dreamzz HR & Consultancy and get matched with jobs across Karnataka." />
      <PageHeader eyebrow="Job Seekers" title="Submit Your Resume" subtitle="Add your profile to our candidate database and get discovered by employers across Karnataka." />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <form onSubmit={submit} className="glass-strong rounded-2xl p-6 sm:p-8 grid gap-4 sm:grid-cols-2">
          <div><Label>Full Name *</Label><Input data-testid="resume-name" required value={form.name} onChange={upd("name")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Mobile *</Label><Input data-testid="resume-mobile" required value={form.mobile} onChange={upd("mobile")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Email *</Label><Input data-testid="resume-email" type="email" required value={form.email} onChange={upd("email")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Current Location</Label><Input data-testid="resume-location" value={form.current_location} onChange={upd("current_location")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Qualification</Label><Input data-testid="resume-qualification" value={form.qualification} onChange={upd("qualification")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Experience</Label><Input data-testid="resume-experience" value={form.experience} onChange={upd("experience")} placeholder="e.g. 3 years" className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Current Job</Label><Input data-testid="resume-current-job" value={form.current_job} onChange={upd("current_job")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Preferred Job</Label><Input data-testid="resume-preferred-job" value={form.preferred_job} onChange={upd("preferred_job")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div className="sm:col-span-2"><Label>Skills (comma separated)</Label><Input data-testid="resume-skills" value={form.skills} onChange={upd("skills")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Preferred Location</Label><Input data-testid="resume-preferred-location" value={form.preferred_location} onChange={upd("preferred_location")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Expected Salary</Label><Input data-testid="resume-salary" value={form.expected_salary} onChange={upd("expected_salary")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Employment Type</Label><Input data-testid="resume-employment-type" value={form.employment_type} onChange={upd("employment_type")} placeholder="Full-time / Contract" className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Languages</Label><Input data-testid="resume-languages" value={form.languages} onChange={upd("languages")} className="bg-white/5 border-white/10 mt-1" /></div>

          <div>
            <Label>Resume (PDF/DOC)</Label>
            <label className="mt-1 flex items-center gap-2 px-4 py-3 rounded-lg border border-dashed border-white/20 cursor-pointer hover:border-brand-emerald bg-white/5">
              <Upload size={18} className="text-brand-emerald" /><span className="text-sm text-slate-400 truncate">{resume ? resume.name : "Upload resume"}</span>
              <input data-testid="resume-file" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0])} />
            </label>
          </div>
          <div>
            <Label>Profile Photo (optional)</Label>
            <label className="mt-1 flex items-center gap-2 px-4 py-3 rounded-lg border border-dashed border-white/20 cursor-pointer hover:border-brand-emerald bg-white/5">
              <Upload size={18} className="text-brand-blue" /><span className="text-sm text-slate-400 truncate">{photo ? photo.name : "Upload photo"}</span>
              <input data-testid="resume-photo" type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} />
            </label>
          </div>

          <label className="sm:col-span-2 flex items-start gap-3 text-sm text-slate-400">
            <Checkbox data-testid="resume-consent" checked={consent} onCheckedChange={setConsent} className="mt-0.5" />
            <span>I accept the Privacy Policy & Terms and consent to be contacted about job opportunities.</span>
          </label>
          <Button data-testid="resume-submit" type="submit" disabled={loading} className="sm:col-span-2 btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold h-12">
            {loading ? "Submitting..." : "Submit Resume"}
          </Button>
        </form>
      </section>
    </PublicLayout>
  );
}
